import { useRef } from "react";
import { LoaderCircle, Volume2, VolumeX } from "lucide-react";

import { useAudio } from "./useAudio";
import "./AudioStatusControl.css";

type AudioControlState =
  | "enabled"
  | "failed"
  | "locked"
  | "muted"
  | "unlocking";

export function AudioStatusControl() {
  const { commands, preferences, snapshot } = useAudio();
  const isEnabled =
    snapshot.unlockState === "unlocked" && !preferences.muted;
  const activationStartedEnabledRef = useRef(isEnabled);
  const hasUnlockFailed =
    snapshot.unlockState === "locked" &&
    snapshot.lastIssue?.reason === "unlock-failed";
  const state: AudioControlState = isEnabled
    ? "enabled"
    : snapshot.unlockState === "unlocking"
      ? "unlocking"
      : snapshot.unlockState === "unlocked"
        ? "muted"
        : hasUnlockFailed
          ? "failed"
          : "locked";
  const visibleLabel =
    state === "enabled"
      ? "声音已开"
      : state === "unlocking"
        ? "正在开启"
        : state === "failed"
          ? "再试声音"
          : "开启声音";
  const actionLabel = isEnabled ? "关闭声音" : visibleLabel;
  const Icon =
    state === "unlocking" ? LoaderCircle : isEnabled ? Volume2 : VolumeX;

  const rememberActivationStart = (): void => {
    activationStartedEnabledRef.current = isEnabled;
  };

  const handleClick = async (): Promise<void> => {
    if (activationStartedEnabledRef.current && isEnabled) {
      commands.setMuted(true);
      activationStartedEnabledRef.current = false;
      return;
    }

    commands.setMuted(false);
    if (snapshot.unlockState !== "unlocked") {
      const result = await commands.unlock();
      activationStartedEnabledRef.current = result.status === "unlocked";
      return;
    }

    activationStartedEnabledRef.current = true;
  };

  return (
    <button
      aria-busy={state === "unlocking" || undefined}
      aria-label={actionLabel}
      aria-pressed={isEnabled}
      className="audio-status-control"
      data-audio-state={state}
      onKeyDown={rememberActivationStart}
      onClick={() => void handleClick()}
      onPointerDown={rememberActivationStart}
      onTouchStart={rememberActivationStart}
      title={actionLabel}
      type="button"
    >
      <Icon aria-hidden="true" className="audio-status-control__icon" />
      <span aria-live="polite" className="audio-status-control__label">
        {visibleLabel}
      </span>
    </button>
  );
}
